import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Card, Button, Modal, Form, Input, Switch, Tag, Space,
  Popconfirm, Typography, Row, Col, Avatar, Tooltip, message, Breadcrumb, Checkbox
} from 'antd';
import {
  UserOutlined, UserAddOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, SafetyCertificateOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined, LockOutlined, MailOutlined, AppstoreOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { getUsers, createUser, updateUser, deleteUser } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const APP_OPTIONS = [
  { label: 'Power Trading Optimization', value: 'trading' },
  { label: 'BESS Sizing Project', value: 'bess' },
  { label: 'DSM Minimization', value: 'dsm_minimization' },
];

export default function UserManagementPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Modal States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchUsersList = () => {
    setLoading(true);
    getUsers()
      .then(res => {
        setUsers(res.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch users:', err);
        message.error('Failed to load user list');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsersList();
  }, []);

  // Filtered Users (Search by name, username, or email)
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const q = searchText.toLowerCase().trim();
      if (!q) return true;
      return (
        (user.username && user.username.toLowerCase().includes(q)) ||
        (user.email && user.email.toLowerCase().includes(q)) ||
        (user.first_name && user.first_name.toLowerCase().includes(q)) ||
        (user.last_name && user.last_name.toLowerCase().includes(q))
      );
    });
  }, [users, searchText]);


  // Handlers
  const handleAddSubmit = async (values) => {
    setSubmitting(true);
    try {
      const allowed_apps = Array.from(new Set([...(values.allowed_apps || []), ...(values.admin_apps || [])]));
      const payload = {
        ...values,
        allowed_apps,
        admin_apps: values.admin_apps || [],
        is_staff: Boolean(values.admin_apps && values.admin_apps.length > 0)
      };
      await createUser(payload);
      message.success(`User '${values.username}' created successfully`);
      setIsAddModalOpen(false);
      addForm.resetFields();
      fetchUsersList();
    } catch (err) {
      console.error('Failed to create user:', err);
      const detail = err.response?.data?.username?.[0] || err.response?.data?.email?.[0] || err.response?.data?.detail || 'Failed to create user';
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      is_active: user.is_active,
      allowed_apps: user.allowed_apps && user.allowed_apps.length > 0
        ? user.allowed_apps
        : (user.is_superuser ? ['trading', 'bess', 'dsm_minimization'] : ['trading']),
      admin_apps: user.admin_apps || (user.is_staff && !user.is_superuser ? (user.allowed_apps || []) : []),
      password: '',
    });
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (values) => {
    if (!editingUser) return;
    setSubmitting(true);
    try {
      const allowed_apps = Array.from(new Set([...(values.allowed_apps || []), ...(values.admin_apps || [])]));
      const payload = {
        ...values,
        allowed_apps,
        admin_apps: values.admin_apps || [],
        is_staff: Boolean(editingUser.is_superuser || (values.admin_apps && values.admin_apps.length > 0))
      };
      if (!payload.password) {
        delete payload.password;
      }
      await updateUser(editingUser.id, payload);
      message.success(`User '${editingUser.username}' updated successfully`);
      setIsEditModalOpen(false);
      setEditingUser(null);
      editForm.resetFields();
      fetchUsersList();
    } catch (err) {
      console.error('Failed to update user:', err);
      const detail = err.response?.data?.detail || 'Failed to update user';
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      message.success(`User '${user.username}' is now ${!user.is_active ? 'Active' : 'Inactive'}`);
      fetchUsersList();
    } catch (err) {
      console.error('Failed to update status:', err);
      message.error('Failed to update user status');
    }
  };

  const handleDelete = async (userId, username) => {
    try {
      await deleteUser(userId);
      message.success(`User '${username}' deleted successfully`);
      fetchUsersList();
    } catch (err) {
      console.error('Failed to delete user:', err);
      message.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Table Columns
  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const fullName = `${record.first_name || ''} ${record.last_name || ''}`.trim();
        const initials = record.first_name ? record.first_name[0].toUpperCase() : record.username[0].toUpperCase();
        return (
          <Space size="middle">
            <Avatar style={{ backgroundColor: record.is_superuser ? '#722ed1' : record.is_staff ? '#1890ff' : '#52c41a' }}>
              {initials}
            </Avatar>
            <div>
              <div style={{ fontWeight: 600, color: '#1f2937' }}>
                {fullName || record.username}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>@{record.username}</Text>
            </div>
          </Space>
        );
      }
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email ? <span style={{ fontSize: 13 }}><MailOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />{email}</span> : <Text type="secondary">-</Text>
    },
    {
      title: 'Role',
      key: 'role',
      render: (_, record) => {
        if (record.is_superuser) {
          return <Tag color="purple" icon={<SafetyCertificateOutlined />}>Superuser</Tag>;
        }
        const adminApps = record.admin_apps || [];
        if (adminApps.length >= 3) {
          return <Tag color="blue" icon={<SafetyCertificateOutlined />}>All-Projects Admin</Tag>;
        }
        if (adminApps.length > 0) {
          const labelMap = { trading: 'Trading', bess: 'BESS', dsm_minimization: 'DSM Min' };
          return (
            <Space size={[0, 4]} wrap>
              {adminApps.map(app => (
                <Tag key={app} color="blue" icon={<SafetyCertificateOutlined />}>
                  Admin ({labelMap[app] || app})
                </Tag>
              ))}
            </Space>
          );
        }
        return <Tag color="default">Standard User</Tag>;
      }
    },
    {
      title: 'Accessible Apps',
      key: 'allowed_apps',
      render: (_, record) => {
        const apps = record.allowed_apps || [];
        if (record.is_superuser && apps.length === 0) {
          return <Tag color="purple">All Applications</Tag>;
        }
        if (apps.length === 0) {
          return <Text type="secondary" style={{ fontStyle: 'italic' }}>None</Text>;
        }
        const colorMap = {
          trading: 'blue',
          bess: 'cyan',
          dsm_minimization: 'orange'
        };
        const labelMap = {
          trading: 'Trading',
          bess: 'BESS',
          dsm_minimization: 'DSM Min'
        };
        return (
          <Space size={[0, 4]} wrap>
            {apps.map(appId => (
              <Tag key={appId} color={colorMap[appId] || 'default'}>
                {labelMap[appId] || appId}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space size="small">
          {record.is_active ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>Active</Tag>
          ) : (
            <Tag color="error" icon={<CloseCircleOutlined />}>Inactive</Tag>
          )}
          <Switch
            size="small"
            checked={record.is_active}
            onChange={() => handleToggleActive(record)}
          />
        </Space>
      )
    },
    {
      title: 'Date Joined',
      dataIndex: 'date_joined',
      key: 'date_joined',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY, HH:mm') : '-'
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY, HH:mm') : <Text type="secondary">Never</Text>
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit User">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete User"
            description={`Are you sure you want to delete user '${record.username}'?`}
            onConfirm={() => handleDelete(record.id, record.username)}
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete User">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Breadcrumb
            items={[
              { title: <a onClick={() => navigate('/app-selection')}>Applications Portal</a> },
              { title: 'User Management' }
            ]}
            style={{ marginBottom: 4 }}
          />
          <Title level={3} style={{ margin: 0, color: '#0f172a' }}>
            <UserOutlined style={{ marginRight: 10, color: '#0052CC' }} />
            User Management
          </Title>
        </div>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app-selection')}
          >
            Back to Portal
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => {
              addForm.resetFields();
              setIsAddModalOpen(true);
            }}
            style={{ backgroundColor: '#0052CC' }}
          >
            Add New User
          </Button>
          <Button
            icon={<span role="img" aria-label="external-link" style={{ marginRight: 6 }}>&#x1F517;</span>}
            onClick={() => {
              const adminUrl = `${window.location.protocol}//${window.location.hostname}:8000`;
              window.open(adminUrl, '_blank');
            }}
          >
            Admin Panel
          </Button>
        </Space>
      </div>


      {/* Main Table Card */}
      <Card
        size="small"
        style={{ borderRadius: 8 }}
        title={
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '8px 0' }}>
            <Input
              placeholder="Search by name, username or email..."
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 360 }}
            />
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total ${t} users` }}
        />
      </Card>

      {/* Modal: Add New User */}
      <Modal
        title={
          <Space>
            <UserAddOutlined style={{ color: '#0052CC' }} />
            <span>Add New User</span>
          </Space>
        }
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onOk={() => addForm.submit()}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddSubmit}
          initialValues={{
            is_active: true,
            is_staff: false,
            is_superuser: false,
            allowed_apps: ['trading', 'bess', 'dsm_minimization']
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: 'Please enter username' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="johndoe" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="john@apraava.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name">
                <Input placeholder="John" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name">
                <Input placeholder="Doe" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter password' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Set temporary password" />
          </Form.Item>

          <Form.Item
            name="allowed_apps"
            label="Accessible Applications"
            rules={[{ required: true, message: 'Please select at least one application' }]}
            extra="Select which applications this user is allowed to see in the App Selection page"
          >
            <Checkbox.Group
              options={APP_OPTIONS}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              onChange={(checkedApps) => {
                const currentAdmins = addForm.getFieldValue('admin_apps') || [];
                const newAdmins = currentAdmins.filter(app => checkedApps.includes(app));
                addForm.setFieldsValue({ admin_apps: newAdmins });
              }}
            />
          </Form.Item>

          <Form.Item
            name="admin_apps"
            label="Project Admin Roles"
            extra="Select which projects this user has administrator privileges for"
          >
            <Checkbox.Group
              options={APP_OPTIONS}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              onChange={(checkedAdmins) => {
                const currentAllowed = addForm.getFieldValue('allowed_apps') || [];
                const newAllowed = Array.from(new Set([...currentAllowed, ...checkedAdmins]));
                addForm.setFieldsValue({ allowed_apps: newAllowed });
              }}
            />
          </Form.Item>

          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={24}>
              <Form.Item name="is_active" label="Active Status" valuePropName="checked" initialValue={true}>
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal: Edit User */}
      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: '#0052CC' }} />
            <span>Edit User: {editingUser?.username}</span>
          </Space>
        }
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingUser(null);
        }}
        onOk={() => editForm.submit()}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: 'Username is required' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[{ type: 'email', message: 'Enter a valid email' }]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="password"
            label="New Password"
            extra="Leave blank to keep existing password"
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            name="allowed_apps"
            label="Accessible Applications"
            rules={[{ required: true, message: 'Please select at least one application' }]}
            extra="Select which applications this user is allowed to see in the App Selection page"
          >
            <Checkbox.Group
              options={APP_OPTIONS}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              onChange={(checkedApps) => {
                const currentAdmins = editForm.getFieldValue('admin_apps') || [];
                const newAdmins = currentAdmins.filter(app => checkedApps.includes(app));
                editForm.setFieldsValue({ admin_apps: newAdmins });
              }}
            />
          </Form.Item>

          <Form.Item
            name="admin_apps"
            label="Project Admin Roles"
            extra="Select which projects this user has administrator privileges for"
          >
            <Checkbox.Group
              options={APP_OPTIONS}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              onChange={(checkedAdmins) => {
                const currentAllowed = editForm.getFieldValue('allowed_apps') || [];
                const newAllowed = Array.from(new Set([...currentAllowed, ...checkedAdmins]));
                editForm.setFieldsValue({ allowed_apps: newAllowed });
              }}
            />
          </Form.Item>

          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={24}>
              <Form.Item name="is_active" label="Active Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
